// effects/personal-details.effects.ts
import { inject } from '@angular/core'
import { createEffect } from '@ngrx/effects'
import { Actions, ofType } from '@ngrx/effects'
import { ExperienceService } from '../../services/experience.service'
import { ToastrService } from 'ngx-toastr'
import { catchError, exhaustMap, map, mergeMap, of, switchMap } from 'rxjs'
import {
  createExperience,
  createExperienceFailure,
  createExperienceSuccess,
  deleteExperience,
  deleteExperienceFailure,
  deleteExperienceSuccess,
  loadExperiences,
  loadExperiencesFailure,
  loadExperiencesSuccess,
  updateExperience,
  updateExperienceFailure,
  updateExperienceSuccess
} from './experience.actions'
import { Store } from '@ngrx/store'
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'

export const loadExperiences$ = createEffect(
  (actions$ = inject(Actions), experienceService = inject(ExperienceService)) =>
    actions$.pipe(
      ofType(loadExperiences),
      switchMap(
        (
          action // Use action.filter here
        ) =>
          experienceService.getExperiences(action.params, action.page, action.limit).pipe(
            map(res =>
              loadExperiencesSuccess({
                experiences: res.experiences,
                totalExperienceYears: res.totalExperienceYears
              })
            ),
            catchError(error => of(loadExperiencesFailure({ error: error.message })))
          )
      )
    ),
  { functional: true }
)

export const createExperience$ = createEffect(
  (
    actions$ = inject(Actions),
    experienceService = inject(ExperienceService),
    toastr = inject(ToastrService),
    store = inject(Store),
    modalService = inject(NgbModal)
  ) =>
    actions$.pipe(
      ofType(createExperience),
      exhaustMap(({ payload }) => {
        // 🔥 RETURN the inner observable here
        return experienceService.createExperience(payload).pipe(
          map(created => {
            toastr.success('Experience created successfully!', 'Success')
            // store.dispatch(loadExperiences({ params: null, page: 1, limit: 10 }))
            modalService.dismissAll()
            return createExperienceSuccess({ experience: created })
          }),
          catchError(error => {
            toastr.error('There was an error creating the Experience.', 'Error')
            return of(createExperienceFailure({ error }))
          })
        )
      })
    ),
  { functional: true, dispatch: true }
)

export const updateExperience$ = createEffect(
  (
    actions$ = inject(Actions),
    experienceService = inject(ExperienceService),
    toastr = inject(ToastrService),
    store = inject(Store),
    modalService = inject(NgbModal)
  ) =>
    actions$.pipe(
      ofType(updateExperience), // 🔁 Listen for updateExperience action
      exhaustMap(({ id, payload }) => {
        return experienceService.updateExperience(id, payload).pipe(
          map(updated => {
            toastr.success('Experience updated successfully!', 'Success')
            // store.dispatch(loadExperiences({ params: null, page: 1, limit: 10 }))
            modalService.dismissAll()
            return updateExperienceSuccess({ experience: updated })
          }),
          catchError(error => {
            toastr.error('There was an error updating the Experience.', 'Error')
            return of(updateExperienceFailure({ error }))
          })
        )
      })
    ),
  { functional: true, dispatch: true }
)

export const deleteExperience$ = createEffect(
  (
    actions$ = inject(Actions),
    experienceService = inject(ExperienceService),
    toastr = inject(ToastrService)
  ) =>
    actions$.pipe(
      ofType(deleteExperience),
      exhaustMap(action =>
        experienceService.deleteExperience(action.id).pipe(
          map(() => {
            toastr.success('Experience deleted successfully!')
            return deleteExperienceSuccess({ id: action.id })
          }),
          catchError(error => {
            toastr.error('Failed to delete Experience: ' + error.message)
            return of(deleteExperienceFailure({ error }))
          })
        )
      )
    ),
  { functional: true }
)

export const experienceEffects = {
  createExperience$,
  loadExperiences$,
  updateExperience$,
  deleteExperience$
}
